import React from "react";
import Seo from "@/components/Seo";

export default function Privacy() {
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="privacy-page">
      <Seo
        title="Integritetspolicy"
        description="Integritetspolicy för Bygglovskompassen enligt GDPR. Hur vi behandlar dina personuppgifter, rättslig grund, AI-behandling, lagringstider och dina rättigheter."
        path="/privacy"
      />
      <h1 className="text-4xl font-semibold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: "Outfit" }}>
        Integritetspolicy
      </h1>
      <p className="text-sm text-zinc-500 mb-8">Senast uppdaterad: 4 juli 2026</p>

      <div className="prose prose-zinc max-w-none space-y-6 text-zinc-800">
        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">1. Personuppgiftsansvarig</h2>
          <p>
            Bygglovskompassen (nedan "vi") är personuppgiftsansvarig för de personuppgifter du lämnar när du använder tjänsten.
            Kontakt: <a href="mailto:info@bygglovkollen.se" className="text-blue-700 underline">info@bygglovkollen.se</a>.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">2. Vilka uppgifter samlar vi in?</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Kontouppgifter:</b> Namn, e-postadress, telefonnummer (om du skapar konto).</li>
            <li><b>Ansökningsuppgifter:</b> Fastighetsbeteckning, adress, projektbeskrivning.</li>
            <li><b>Dokument:</b> Ritningar, bilder och handlingar du laddar upp.</li>
            <li><b>Chatt-loggar:</b> Frågor du ställer till AI-rådgivaren.</li>
            <li><b>Teknisk information:</b> IP-adress, webbläsare, tidsstämplar (via loggar).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">3. Rättslig grund</h2>
          <p>Vi behandlar personuppgifter med följande rättsliga grunder enligt GDPR artikel 6:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Avtal (art. 6.1.b):</b> För att leverera tjänsten du begärt.</li>
            <li><b>Berättigat intresse (art. 6.1.f):</b> För säkerhet, missbruksförebyggande och tjänsteförbättringar.</li>
            <li><b>Samtycke (art. 6.1.a):</b> För icke-nödvändiga cookies och marknadskommunikation.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">4. AI-behandling</h2>
          <p>
            Frågor du ställer till AI-rådgivaren skickas till Anthropic (Claude) och/eller Google (Gemini) för att generera svar.
            Vi delar <b>inte</b> ditt namn, adress eller personnummer med AI-leverantörerna. Chatt-loggarna sparas i vår databas
            i upp till 12 månader för att förbättra tjänsten.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">5. Delning med tredje part</h2>
          <p>Dina uppgifter delas endast med:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>AI-leverantörer</b> (Anthropic, Google) – för att bearbeta AI-frågor.</li>
            <li><b>Molnleverantörer</b> för lagring och drift (t.ex. MongoDB Atlas, Emergent).</li>
            <li><b>E-postleverantör</b> (Resend) – när du väljer att skicka något via mail.</li>
            <li><b>Betalningsleverantör</b> (Stripe) – vid kommunavtal (gäller inte gratistjänsten).</li>
          </ul>
          <p>Vi säljer <b>aldrig</b> dina uppgifter till tredje part.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">6. Lagringstid</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Aktiva konton: så länge du är kund + 12 månader.</li>
            <li>Chatt-loggar: 12 månader.</li>
            <li>Dokument: så länge ärendet är öppet + 24 månader.</li>
            <li>Fakturor/kvitton: 7 år (bokföringslagen).</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">7. Dina rättigheter</h2>
          <p>Enligt GDPR har du rätt att:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Få information om vilka uppgifter vi har om dig (registerutdrag).</li>
            <li>Rätta felaktiga uppgifter.</li>
            <li>Radera uppgifter (rätten att bli glömd).</li>
            <li>Begränsa eller invända mot behandling.</li>
            <li>Överföra dina uppgifter (dataportabilitet).</li>
            <li>Klaga hos Integritetsskyddsmyndigheten (imy.se).</li>
          </ul>
          <p>Skicka begäran till: <a href="mailto:info@bygglovkollen.se" className="text-blue-700 underline">info@bygglovkollen.se</a></p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">8. Cookies</h2>
          <p>Vi använder följande typer av cookies:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><b>Nödvändiga:</b> Sessions-cookie för inloggning (kan inte stängas av).</li>
            <li><b>Preferenser:</b> Sparar val som språk och tema (endast om du samtycker).</li>
            <li><b>Analys:</b> Anonymiserad besöksstatistik (endast om du samtycker).</li>
          </ul>
          <p>Du kan ändra dina cookieinställningar när som helst via länken längst ner på sidan.</p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">9. Säkerhet</h2>
          <p>
            Vi använder TLS-kryptering, bcrypt-hashade lösenord, JWT-baserade sessioner och
            åtkomstkontroll per användare. Data lagras på servrar inom EU/EES.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold mt-8 mb-2">10. Ändringar</h2>
          <p>
            Vi kan uppdatera denna policy. Väsentliga ändringar meddelas via startsidan
            eller e-post minst 30 dagar i förväg.
          </p>
        </section>
      </div>
    </div>
  );
}
